import React, { useEffect, useRef, useState } from 'react';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist/legacy/build/pdf';

// Render the PDF ourselves (canvas) instead of relying on the browser to embed
// it — so the report always shows inline, regardless of the viewer's
// "Download PDFs instead of opening" Chrome setting. The worker is served as a
// static file from /public (copied from pdfjs-dist) so webpack never bundles it
// — that bundling was what crashed react-pdf with "process is not defined".
GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

export default function PdfViewer({ url, height }) {
  const containerRef = useRef(null);
  const [status, setStatus] = useState('loading'); // loading | done | error
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;
    if (container) container.innerHTML = '';
    setStatus('loading');
    setErrorMsg('');

    const task = getDocument(url);
    task.promise
      .then(async (pdf) => {
        const dpr = window.devicePixelRatio || 1;
        for (let n = 1; n <= pdf.numPages; n++) {
          if (cancelled) return;
          const page = await pdf.getPage(n);
          const containerWidth = (container && container.clientWidth) || 800;
          const unscaled = page.getViewport({ scale: 1 });
          const scale = Math.max(0.2, Math.min(2, containerWidth / unscaled.width));
          const viewport = page.getViewport({ scale });

          const canvas = document.createElement('canvas');
          canvas.width = Math.floor(viewport.width * dpr);
          canvas.height = Math.floor(viewport.height * dpr);
          canvas.style.width = '100%';
          canvas.style.height = 'auto';
          canvas.style.display = 'block';
          canvas.style.margin = '0 auto 10px';
          canvas.style.borderRadius = '4px';
          canvas.style.boxShadow = '0 2px 10px rgba(0,0,0,0.35)';
          const ctx = canvas.getContext('2d');
          if (cancelled) return;
          if (container) container.appendChild(canvas);
          await page.render({
            canvasContext: ctx,
            viewport,
            transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : null,
          }).promise;
        }
        if (!cancelled) setStatus('done');
      })
      .catch((e) => {
        if (!cancelled) { setErrorMsg((e && e.message) || 'Failed to load PDF'); setStatus('error'); }
      });

    return () => {
      cancelled = true;
      try { task.destroy(); } catch (e) { /* noop */ }
    };
  }, [url]);

  return (
    <div style={{ height: height || '70vh', overflowY: 'auto', background: '#525659', borderRadius: 8, padding: 14, marginTop: 8 }}>
      {status === 'loading' && (
        <div style={{ color: 'rgba(255,255,255,0.85)', textAlign: 'center', padding: 24, fontSize: 13 }}>Loading report…</div>
      )}
      {status === 'error' && (
        <div style={{ color: 'rgba(255,255,255,0.9)', textAlign: 'center', padding: 24, fontSize: 13 }}>
          Couldn't render the report ({errorMsg}). Use the Download button below.
        </div>
      )}
      <div ref={containerRef}></div>
    </div>
  );
}
